import Alpine from 'alpinejs';

// Below Tailwind's `md` the navigation leaves the header bar and covers the page. Only then
// may the rest of the document be taken out of the accessibility tree and the tab order.
// `window.device.isMobile` is a wider range (< 1024px) and would also match the breakpoints
// where the menu sits inside the bar and the page stays visible.
const MENU_COVERS_PAGE_QUERY = '(max-width: 767px)';

Alpine.data('header', () => ({
    init() {
        // The header is cached once for the whole site, therefore we set the active state via JS
        let activeLink = this.$root.querySelector(
            'a[href="' + location.pathname + '"], a[href="' + location.origin + '/' + location.pathname + '"]'
        );
        let activeLi = activeLink?.closest('li');
        while (activeLi) {
            activeLi.classList.add('navigation__menu-item--state-active');
            activeLi = activeLi.parentElement.closest('li');
        }
        new Header(this.$root);
    },
}));

class Header {
    constructor(element) {
        this.element = element;
        this.isScrolled = false;
        this.classes = {
            headerIsScrolled: 'header--is-scrolled',
            navigation: 'navigation',
            submenu: 'navigation__submenu',
            toggleButton: 'header__toggle-button',
            openSubmenuItem: 'navigation__menu-item--is-open',
            toggleSubmenuItems: 'header__menu-item--is-toggle',
        };
        this.isMainMenuOpen = false;
        this.isSubmenuOpen = false;
        this.navigation = element.querySelector(`.${this.classes.navigation}`);
        this.headerToggleButton = element.querySelector(`.${this.classes.toggleButton}`);

        this.handleScrolledState();

        // click outside of header
        window.addEventListener('click', (e) => {
            let el = e.target;

            if (!this.element.contains(el)) {
                this.closeMainMenu();
                this.closeSubmenu();
            }
        });

        // main menu toggle event listener/handler
        window.addEventListener('click', (e) => {
            let el = e.target;

            if (this.headerToggleButton.contains(el)) {
                this.closeSubmenu();
                this.isElementOpen(this.headerToggleButton) ? this.closeMainMenu() : this.openMainMenu();
            }

            if (!this.element.contains(el)) {
                this.closeMainMenu();
            }
        });

        if (!window.device.isMobile) return;

        // submenu toggle event listener/handler
        window.addEventListener('click', (e) => {
            let el = e.target;

            let toggleItem = !el.closest(`.${this.classes.submenu}`) // if "el" is not submenu item (first lvl item)
                ? el.closest(`.${this.classes.toggleSubmenuItems}`)
                : null;

            if (!toggleItem) return;

            e.preventDefault();
            e.stopPropagation();

            this[this.isElementOpen(toggleItem) ? 'closeSubmenu' : 'openSubmenu'](toggleItem);
        });
    }

    handleScrolledState() {
        window.addEventListener('scroll', () =>
            requestAnimationFrame(() => {
                this.element.classList[window.scrollY < 38 ? 'remove' : 'add'](`${this.classes.headerIsScrolled}`);
            })
        );
        this.element.classList[window.scrollY < 38 ? 'remove' : 'add'](`${this.classes.headerIsScrolled}`);
    }

    openMainMenu() {
        this.element.classList.add('header--is-open');
        // The state belongs on the control, not on the landmark around it.
        this.headerToggleButton.setAttribute('aria-expanded', 'true');
        if (window.device.isMobile) {
            window.dispatchEvent(new CustomEvent('screenScrollLock'));
        }
        if (window.matchMedia(MENU_COVERS_PAGE_QUERY).matches) {
            // Keeps screen readers and the tab order inside the open menu.
            this.setRestOfPageInert(true);
        }
    }

    closeMainMenu() {
        this.headerToggleButton.setAttribute('aria-expanded', 'false');
        this.element.classList.remove('header--is-open');
        // Always released, so a resize while the menu is open cannot leave the page inert.
        this.setRestOfPageInert(false);
        if (window.device.isMobile) {
            window.dispatchEvent(new CustomEvent('screenScrollUnlock'));
        }
    }

    setRestOfPageInert(isInert) {
        document.querySelectorAll('body > *:not(.header)').forEach((element) => {
            if (element.contains(this.element)) {
                return;
            }
            element.toggleAttribute('inert', isInert);
        });
    }

    openSubmenu(element) {
        if (!element) return;

        // close all items except the one that was clicked
        Array.from(this.element.querySelectorAll(`.${this.classes.openSubmenuItem}`)).forEach((_element) => {
            if (!_element.isSameNode(element)) this.closeSubmenu(_element);
        });

        // open sub menu on toggle click
        element.setAttribute('aria-expanded', 'true');
        element.classList.add(`${this.classes.openSubmenuItem}`);
    }

    closeSubmenu(element) {
        element = element || null;

        if (!element) {
            Array.from(this.element.querySelectorAll(`.${this.classes.openSubmenuItem}`)).forEach((openSubmenuItem) => {
                openSubmenuItem.setAttribute('aria-expanded', 'false');
                openSubmenuItem.classList.remove(`${this.classes.openSubmenuItem}`);
            });

            return;
        }

        element.setAttribute('aria-expanded', 'false');
        element.classList.remove(`${this.classes.openSubmenuItem}`);
    }

    toggleSubmenu(element) {
        if (!element) return;

        this[element.isOpen ? 'closeSubmenu' : 'openSubmenu'](element);
    }

    isElementOpen(element) {
        if (!element) return;
        return element.getAttribute('aria-expanded') === 'true';
    }
}
